import { PageHeader } from '@app/components/layout/page/components/page-header';
import Page from '@app/components/layout/page/page';
import { useGetDashboard } from '@shared/api';
import Text from '@shared/components/content/text';
import React from 'react';
import AverageLifeCycleEmissions from './components/average-life-cycle-emissions';
import DashboardSection from './components/dashboard-section';
import ProductRatingsBreakdown from './components/product-ratings-breakdown';
import ProductsBox from './components/products';
import DashboardEmptyPage from './dashboard-empty-page';
import ProductDashboardHeader from './sections/product-dashboard-header';

export type TProductDashboard = any;

const ProductDashboardContext = React.createContext<TProductDashboard | object>(
  null
);

export function useProductDashboard() {
  return React.useContext(ProductDashboardContext) as TProductDashboard;
}

const ProductDashboardPage: React.FC = () => {
  const { data: dashboardData, status } = useGetDashboard();

  const context: TProductDashboard = {
    ...dashboardData,
  };

  return (
    <Page name="Dashboard" status={status}>
      {!!dashboardData && (
        <ProductDashboardContext.Provider value={context}>
          <PageHeader name="Product dashboard" includeBreadcrumb={false} />

          {dashboardData.totalNumberOfProducts > 0 ? (
            <div className="grid gap-x-2 gap-y-3 pl-0">
              <ProductDashboardHeader />
              {dashboardData.numberProductsComplete > 0 ? (
                <>
                  <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ProductRatingsBreakdown
                      productEmissionsCategoryBreakdown={
                        dashboardData.productEmissionsCategoryBreakdown
                      }
                      averageEmissionsCategory={
                        dashboardData!.averageEmissionsCategory
                      }
                    />
                    <AverageLifeCycleEmissions
                      averageLifeCycleStageEmissionsPerKg={
                        dashboardData!.averageLifeCycleStageEmissionsPerKg
                      }
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {dashboardData.products !== null ? (
                      <DashboardSection
                        title="Your Products"
                        tooltipContent="Your Products"
                      >
                        <ProductsBox
                          items={dashboardData!.products}
                          numShown={5}
                          itemsType="Products"
                        />
                      </DashboardSection>
                    ) : (
                      <div>
                        <DashboardSection
                          title="Lowest Emissions Products"
                          tooltipContent="Lowest Emissions Product"
                        >
                          <ProductsBox
                            items={dashboardData!.lowestEmissionsProducts}
                            numShown={3}
                            itemsType="Products"
                          />
                        </DashboardSection>

                        <DashboardSection
                          title="Highest Emissions Products"
                          tooltipContent="Highest Emissions Product"
                        >
                          <ProductsBox
                            items={dashboardData!.highestEmissionsProducts}
                            numShown={3}
                            itemsType="Products"
                          />
                        </DashboardSection>
                      </div>
                    )}
                    <DashboardSection
                      title="Most Used Ingredients"
                      tooltipContent="Most Used Ingredients"
                    >
                      <ProductsBox
                        items={dashboardData!.mostUsedFoods}
                        numShown={7}
                        itemsType="Foods"
                      />
                    </DashboardSection>
                  </div>
                </>
              ) : (
                <div className="flex size-full items-center justify-center">
                  <Text variant="section">
                    Data about your products will appear here once a product has
                    been assessed.
                  </Text>
                </div>
              )}
            </div>
          ) : (
            <DashboardEmptyPage />
          )}
        </ProductDashboardContext.Provider>
      )}
    </Page>
  );
};

export default ProductDashboardPage;
